// 移除循環導入 - 不直接導入 speech.ts
import { supabase } from '@/lib/supabase'
import { audioCache } from './audioCache'
import { playAudioIOSOptimized } from './audioPlayer'

// VOICEVOX 服務配置
const VOICEVOX_BASE_URL = process.env.NEXT_PUBLIC_VOICEVOX_URL || 'https://tts.jlpt-aitutor.com'

// TTS 服務通用介面
export interface TTSService {
  speak(text: string, options?: {
    voice?: 'male' | 'female',
    language?: string,
    pitch?: number,
    rate?: number,
    voice_id?: number,
    fallbackToBrowser?: boolean,
    role?: string,
    userSessionId?: string,  // 新增：用戶會話 ID
    level?: string  // 新增：JLPT 等級 (n5, n4, n3, n2, n1)
  }): Promise<void>
  
  getAvailableVoices(): Promise<Array<{
    id: string,
    name: string,
    language: string,
    gender: 'male' | 'female'
  }>>

  isServiceAvailable?(): Promise<{
    available: boolean,
    message?: string,
    details?: any
  }>
}

// 瀏覽器原生 TTS 服務
export class BrowserTTSService implements TTSService {
  private getVoicesByGender(type: 'male' | 'female'): SpeechSynthesisVoice[] {
    const genderKeywords = {
      male: ['male', '男', 'man', 'Ichiro', 'Keita', 'deep'],
      female: ['female', '女', 'woman', 'Haruka', 'Sayaka', 'soft']
    }

    const voices = window.speechSynthesis.getVoices()
    return voices.filter(voice => 
      genderKeywords[type].some(keyword => 
        voice.name.toLowerCase().includes(keyword.toLowerCase())
      )
    )
  }

  async speak(text: string, options: {
    voice?: 'male' | 'female',
    language?: string,
    pitch?: number,
    rate?: number
  } = {}): Promise<void> {
    return new Promise((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance(text)
      
      // 選擇語音
      const voices = this.getVoicesByGender(options.voice || 'female')
      if (voices.length > 0) {
        utterance.voice = voices[0]
      }

      // 設置參數
      utterance.lang = options.language || 'ja-JP'
      utterance.pitch = options.pitch || 1.0
      utterance.rate = options.rate || 1.0

      utterance.onend = () => resolve()
      utterance.onerror = (error) => reject(error)

      window.speechSynthesis.speak(utterance)
    })
  }

  async getAvailableVoices(): Promise<Array<{
    id: string,
    name: string,
    language: string,
    gender: 'male' | 'female'
  }>> {
    const voices = window.speechSynthesis.getVoices()
    return voices.map(voice => ({
      id: voice.voiceURI,
      name: voice.name,
      language: voice.lang,
      gender: this.getVoicesByGender('male').includes(voice) ? 'male' : 'female'
    }))
  }

  async isServiceAvailable(): Promise<{
    available: boolean,
    message?: string,
    details?: any
  }> {
    return {
      available: typeof window !== 'undefined' && 'speechSynthesis' in window,
      message: '瀏覽器語音服務',
      details: {}
    }
  }
}

// 預設語音配置
const DEFAULT_VOICES = {
  male: {
    voice_id: 13,  // 青山龍星 ノーマル
    name: '青山龍星（通常）'
  },
  female: {
    voice_id: 2,   // 四国めたん ノーマル
    name: '四国めたん（通常）'
  }
}

// 角色名稱轉換函數
async function getCustomVoiceRoleName(
  voiceName: string, 
  userId?: string
): Promise<string> {
  // 如果沒有提供用戶ID，直接返回原始名稱
  if (!userId) return voiceName

  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return voiceName

    const { data: profileData, error } = await supabase
      .from('profiles')
      .select('custom_voice_roles')
      .eq('id', user.id)
      .single()

    if (error || !(profileData as any)?.custom_voice_roles) {
      return voiceName
    }

    // 檢查是否有自定義名稱
    return (profileData as any).custom_voice_roles[voiceName] || voiceName
  } catch (error) {
    console.error('獲取自定義角色名稱失敗:', error)
    return voiceName
  }
}

export class VoicevoxTTSService implements TTSService {
  // 可配置的 Voicevox 服務地址和部署選項
  private baseUrl: string
  private deploymentOptions = {
    local: VOICEVOX_BASE_URL,
    docker: VOICEVOX_BASE_URL,
    remote: VOICEVOX_BASE_URL, // 使用統一的 VOICEVOX URL
    cloud: {
      aws: VOICEVOX_BASE_URL,
      gcp: VOICEVOX_BASE_URL,
      azure: VOICEVOX_BASE_URL
    }
  }

  constructor(options?: {
    baseUrl?: string,
    deployment?: 'local' | 'docker' | 'remote' | 'cloud',
    cloudProvider?: 'aws' | 'gcp' | 'azure'
  }) {
    // 設置基礎 URL - 優先使用環境變數
    if (options?.baseUrl) {
      this.baseUrl = options.baseUrl
    } else if (process.env.VOICEVOX_API_URL) {
      // 使用環境變數中的 VOICEVOX API URL
      this.baseUrl = process.env.VOICEVOX_API_URL
      // console.log('使用環境變數 VOICEVOX_API_URL:', this.baseUrl)
    } else {
      // 根據部署類型選擇默認 URL
      const deployment = options?.deployment || 'local'
      const cloudProvider = options?.cloudProvider || 'aws'
      
      switch (deployment) {
        case 'local':
          this.baseUrl = this.deploymentOptions.local
          break
        case 'docker':
          this.baseUrl = this.deploymentOptions.docker
          break
        case 'remote':
          this.baseUrl = this.deploymentOptions.remote || this.deploymentOptions.local
          break
        case 'cloud':
          this.baseUrl = this.deploymentOptions.cloud[cloudProvider] || this.deploymentOptions.local
          break
        default:
          this.baseUrl = this.deploymentOptions.local
      }
    }

    // console.log(`Voicevox 服務地址：${this.baseUrl}`)
  }

  // Voicevox 配音角色映射
  private voiceMap = {
    male: [
      { id: 13, name: '青山龍星', displayName: '青山龍星（通常）', style: 'ノーマル' },
      { id: 81, name: '青山龍星-熱血', displayName: '青山龍星-熱血', style: '熱血' },
      { id: 82, name: '青山龍星-不機嫌', displayName: '青山龍星-不機嫌', style: '不機嫌' },
      { id: 83, name: '青山龍星-喜び', displayName: '青山龍星-喜び', style: '喜び' },
      { id: 84, name: '青山龍星-しっとり', displayName: '青山龍星-しっとり', style: 'しっとり' },
      { id: 85, name: '青山龍星-かなしみ', displayName: '青山龍星-かなしみ', style: 'かなしみ' },
      { id: 86, name: '青山龍星-囁き', displayName: '青山龍星-囁き', style: '囁き' },
      { id: 11, name: '玄野武宏', displayName: '玄野武宏（通常）', style: 'ノーマル' },
      { id: 39, name: '玄野武宏-喜び', displayName: '玄野武宏-喜び', style: '喜び' },
      { id: 40, name: '玄野武宏-ツンギレ', displayName: '玄野武宏-ツンギレ', style: 'ツンギレ' },
      { id: 41, name: '玄野武宏-悲しみ', displayName: '玄野武宏-悲しみ', style: '悲しみ' }
    ],
    female: [
      { id: 2, name: '琴詠ニア', displayName: '琴詠ニア', style: 'normal' },
      { id: 94, name: '中部つるぎ', displayName: '中部つるぎ', style: 'normal' }
    ]
  }

  async isServiceAvailable(): Promise<{
    available: boolean,
    message?: string,
    details?: {
      speakersCount?: number,
      supportedStyles?: string[],
      diagnosis?: string[]
    }
  }> {
    try {
      // 在瀏覽器環境中使用代理 API，在服務器環境中直接連接
      const apiUrl = typeof window !== 'undefined' 
        ? `/api/voicevox/speakers`  // 瀏覽器環境使用本地 API 代理
        : `${this.baseUrl}/api/voicevox/speakers` // 服務器環境直接連接
        
      const response = await fetch(apiUrl, {
        method: 'GET',
        signal: AbortSignal.timeout(10000) // 增加超時時間到 10 秒
      })

      if (!response.ok) {
        console.error(`Voicevox 服務返回錯誤：${response.status} ${response.statusText}`)
        return {
          available: false,
          message: `服務返回錯誤：${response.status} ${response.statusText}`,
          details: {
            diagnosis: [
              '1. 確保 VOICEVOX 應用程式已運行',
              `2. 檢查 ${VOICEVOX_BASE_URL} 是否可訪問`,
              '3. 檢查防火牆是否阻止本地服務',
              '4. 嘗試重新啟動 VOICEVOX 應用程式',
              `5. 當前服務狀態：${response.status}`
            ]
          }
        }
      }

      const speakers = await response.json()
      const availableStyles = speakers.flatMap((speaker: any) =>
        speaker.styles.map((style: any) => style.name)
      )

      return {
        available: true,
        message: '服務正常運行',
        details: {
          speakersCount: speakers.length,
          supportedStyles: availableStyles
        }
      }
    } catch (error: any) {
      console.error('Voicevox 服務診斷失敗:', error)
      
      // 詳細的錯誤診斷
      const errorDiagnosis = [
        '1. VOICEVOX 應用程式未運行',
        '2. 網路連線問題',
        '3. 防火牆阻止',
        '4. 連接埠 50021 被佔用',
        `5. 具體錯誤：${error.message}`
      ]

      // 嘗試檢查端口
      try {
        // ✅ 使用內部 API 代理檢查版本
        const portCheckResponse = await fetch('/api/voicevox/version', { 
          method: 'GET', 
          signal: AbortSignal.timeout(10000) 
        })
        
        if (portCheckResponse.ok) {
          errorDiagnosis.push('6. 端口可訪問，但服務未正常響應')
        }
      } catch {}

      return {
        available: false,
        message: `服務不可用：${error.message}`,
        details: {
          diagnosis: errorDiagnosis
        }
      }
    }
  }

  async speak(text: string, options: {
    voice?: 'male' | 'female',
    language?: string,
    voice_id?: number | string,
    fallbackToBrowser?: boolean,
    role?: string,  // 新增角色參數
    userSessionId?: string,  // 新增：用戶會話 ID
    level?: string  // 新增：JLPT 等級 (n5, n4, n3, n2, n1)
  } = {}): Promise<void> {
    const { 
      voice = 'female', 
      language = 'ja-JP', 
      voice_id,
      fallbackToBrowser = false, // 默認不允許回退到瀏覽器語音
      role,  // 新增角色參數
      level  // 新增：JLPT 等級
    } = options

    // console.group('🎙️ Voicevox 語音選擇詳情')
    // console.log('輸入參數:', { voice, language, voice_id, role })

    try {
      // 優先使用 Voicevox（已移除 isServiceAvailable 檢查以提升效能）
      // 如果提供了具體的 voice_id，通過 API 代理使用
      if (voice_id !== undefined) {
          // console.log(`🎤 使用指定語音 ID: ${voice_id}`)
        try {
          // 檢查音頻快取（包含 level，因為不同 level 會有不同語速）
          const cacheKey = `${text}_${voice_id}${level ? `_${level}` : ''}`
          const cachedAudio = audioCache.get(text, Number(voice_id), level)
          
          if (cachedAudio) {
            // console.log(`🎵 使用快取音頻: ${cacheKey}`)
            const audioBlob = new Blob([cachedAudio], { type: 'audio/wav' })
            const audioUrl = URL.createObjectURL(audioBlob)
            
            // 使用 iOS 優化的播放函數
            await playAudioIOSOptimized(audioUrl)
            
            // console.log(`🎤 成功播放快取語音 ID: ${voice_id}`)
            // console.groupEnd()
            return
          }

          // 沒有快取，生成新音頻
          // console.log(`🎵 生成新音頻: ${cacheKey}`)
          
          // 在瀏覽器環境中使用代理 API
          const queryApiUrl = typeof window !== 'undefined'
            ? `/api/voicevox/audio_query`
            : `${this.baseUrl}/api/voicevox/audio_query`
          
          const queryResponse = await fetch(queryApiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              text: text,
              speaker: voice_id.toString(),
              level: level  // 傳遞 JLPT 等級到 API 層
            }),
            signal: AbortSignal.timeout(8000) // 8秒超時
          })

          if (!queryResponse.ok) {
            throw new Error(`查詢音頻失敗：${queryResponse.status} ${queryResponse.statusText}`)
          }

          const queryData = await queryResponse.json()

          // 在瀏覽器環境中使用代理 API
          const synthesisApiUrl = typeof window !== 'undefined'
            ? `/api/voicevox/synthesis?speaker=${voice_id}`
            : `${this.baseUrl}/api/voicevox/synthesis?speaker=${voice_id}`
            

          const audioResponse = await fetch(synthesisApiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(queryData),
            signal: AbortSignal.timeout(8000) // 8秒超時
          })

          if (!audioResponse.ok) {
            throw new Error(`生成音頻失敗：${audioResponse.status} ${audioResponse.statusText}`)
          }

          const audioBuffer = await audioResponse.arrayBuffer()
          
          // 保存到快取（包含 level，因為不同 level 會有不同語速）
          audioCache.set(text, Number(voice_id), audioBuffer, level)
          // console.log(`💾 音頻已快取: ${cacheKey}`)

          const audioBlob = new Blob([audioBuffer], { type: 'audio/wav' })
          const audioUrl = URL.createObjectURL(audioBlob)
          
          // 使用 iOS 優化的播放函數
          await playAudioIOSOptimized(audioUrl)

          // console.log(`🎤 成功播放指定語音 ID: ${voice_id}`)
          // console.groupEnd()
          return
        } catch (voicevoxError) {
          console.warn('Voicevox 指定語音播放失敗', voicevoxError)
          
          // 如果不允許回退，拋出錯誤
          if (!fallbackToBrowser) {
            console.groupEnd()
            throw voicevoxError
          }
        }
      }

      // 使用預設的性別語音（基礎語音服務不處理角色映射）
      const voices = this.voiceMap[voice]
      const selectedVoice = voices.find(v => v.style === 'ノーマル' || v.style === 'normal') || voices[0]
      
      if (!selectedVoice) {
        console.groupEnd()
        throw new Error(`無法找到適合的語音：性別=${voice}`)
      }

      // console.log(`🎤 使用預設性別語音：`, selectedVoice)

      try {
        // 在瀏覽器環境中使用代理 API
        const queryApiUrl = typeof window !== 'undefined'
          ? `/api/voicevox/audio_query`
          : `${this.baseUrl}/api/voicevox/audio_query`
          
        const queryResponse = typeof window !== 'undefined'
          ? await fetch(queryApiUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                text: text,
                speaker: selectedVoice.id,
                level: level  // 傳遞 JLPT 等級到 API 層
              }),
              signal: AbortSignal.timeout(8000) // 8秒超時
            })
          : await fetch(`${queryApiUrl}?text=${encodeURIComponent(text)}&speaker=${selectedVoice.id}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              signal: AbortSignal.timeout(8000) // 8秒超時
            })

        if (!queryResponse.ok) {
          throw new Error(`查詢音頻失敗：${queryResponse.status} ${queryResponse.statusText}`)
        }

        const queryData = await queryResponse.json()

        // 在瀏覽器環境中使用代理 API
        const synthesisApiUrl = typeof window !== 'undefined'
          ? `/api/voicevox/synthesis?speaker=${selectedVoice.id}`
          : `${this.baseUrl}/api/voicevox/synthesis?speaker=${selectedVoice.id}`

        const audioResponse = await fetch(synthesisApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(queryData),
          signal: AbortSignal.timeout(8000) // 8秒超時
        })

        if (!audioResponse.ok) {
          throw new Error(`生成音頻失敗：${audioResponse.status} ${audioResponse.statusText}`)
        }

        const audioBlob = await audioResponse.blob()
        const audioUrl = URL.createObjectURL(audioBlob)

        // 使用 iOS 優化的播放函數
        await playAudioIOSOptimized(audioUrl)

        // console.log(`🎤 成功播放語音 ID: ${selectedVoice.id}`)
        // console.groupEnd()
        return
      } catch (voicevoxError) {
        console.warn('Voicevox 語音播放失敗', voicevoxError)
        
        // 如果不允許回退，拋出錯誤
        if (!fallbackToBrowser) {
          console.groupEnd()
          throw voicevoxError
        }
      }

      // 如果 Voicevox 不可用，使用瀏覽器語音
      if (fallbackToBrowser) {
        // console.log('🎤 Voicevox 不可用，回退到瀏覽器語音')
        // console.groupEnd()
        
        // 內聯瀏覽器語音播放，避免循環導入
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance(text)
          utterance.lang = 'ja-JP'
          utterance.rate = 1.0
          utterance.pitch = voice === 'male' ? 0.6 : 1.0
          utterance.volume = 0.8
          
          // 選擇合適的語音
          const voices = window.speechSynthesis.getVoices()
          const japaneseVoice = voices.find(v => v.lang === 'ja-JP')
          if (japaneseVoice) {
            utterance.voice = japaneseVoice
          }
          
          window.speechSynthesis.speak(utterance)
          
          return new Promise<void>((resolve) => {
            utterance.onend = () => resolve()
            utterance.onerror = () => resolve()
            setTimeout(() => resolve(), 5000) // 5秒超時
          })
        }
      } else {
        console.groupEnd()
        throw new Error('Voicevox 服務不可用，且不允許回退到瀏覽器語音')
      }
    } catch (error) {
      console.error('語音播放失敗', error)
      console.groupEnd()
      throw error
    }
  }

  // 獲取預設語音
  getDefaultVoice(type: 'male' | 'female' = 'female'): { 
    id: number, 
    name: string, 
    type: 'male' | 'female' 
  } {
    const defaultVoice = DEFAULT_VOICES[type]
    return {
      id: defaultVoice.voice_id,
      name: defaultVoice.name,
      type: type
    }
  }

  // 獲取所有可用語音
  async getAvailableVoices(): Promise<Array<{
    id: string,
    name: string,
    language: string,
    gender: 'male' | 'female',
    style?: string
  }>> {
    return [
      ...this.voiceMap.male.map(voice => ({
        id: voice.id.toString(),
        name: voice.name,
        language: 'ja-JP',
        gender: 'male' as const,
        style: voice.style
      })),
      ...this.voiceMap.female.map(voice => ({
        id: voice.id.toString(),
        name: voice.name,
        language: 'ja-JP',
        gender: 'female' as const,
        style: voice.style
      }))
    ]
  }
}

// 定義 VOICEVOX 語音常量
export const VOICEVOX_VOICES = {
  male: [
    { 
      id: 13, 
      name: '青山龍星', 
      style: 'ノーマル',
      description: '預設男性語音，自然平穩'
    },
    { 
      id: 81, 
      name: '青山龍星', 
      style: '熱血',
      description: '充滿激情和熱情的語音'
    },
    { 
      id: 82, 
      name: '青山龍星', 
      style: '不機嫌', 
      description: '略帶不耐煩和冷淡的語音'
    },
    { 
      id: 83, 
      name: '青山龍星', 
      style: '喜び', 
      description: '充滿喜悅和興奮的語音'
    },
    { 
      id: 84, 
      name: '青山龍星', 
      style: 'しっとり', 
      description: '溫柔、平靜的語音'
    },
    { 
      id: 85, 
      name: '青山龍星', 
      style: 'かなしみ', 
      description: '帶有哀傷和感傷的語音'
    },
    { 
      id: 86, 
      name: '青山龍星', 
      style: '囁き', 
      description: '輕聲細語、低語的語音'
    },
    { 
      id: 11, 
      name: '玄野武宏', 
      style: 'ノーマル',
      description: '預設男性語音，穩重有力'
    },
    { 
      id: 39, 
      name: '玄野武宏', 
      style: '喜び', 
      description: '充滿喜悅和興奮的語音'
    },
    { 
      id: 40, 
      name: '玄野武宏', 
      style: 'ツンギレ', 
      description: '傲嬌、略帶刻薄的語音'
    },
    { 
      id: 41, 
      name: '玄野武宏', 
      style: '悲しみ', 
      description: '帶有悲傷和低落情緒的語音'
    }
  ],
  female: [
    { 
      id: 2, 
      name: '四国めたん', 
      style: 'ノーマル',
      description: '預設女性語音，自然清晰'
    },
    { 
      id: 94, 
      name: '中部つるぎ', 
      style: 'ノーマル',
      description: '成熟、專業的女性語音'
    },
    { 
      id: 74, 
      name: '琴詠ニア', 
      style: 'ノーマル',
      description: '年輕、清亮的女性語音'
    }
  ]
}

// 獲取 VIP 語音選擇
export function getVIPVoices(gender: 'male' | 'female') {
  const vipMap = {
    male: [
      { id: 83, name: '青山龍星', style: '喜び', description: 'VIP：喜悅情緒' },
      { id: 81, name: '青山龍星', style: '熱血', description: 'VIP：熱血激情' }
    ],
    female: [
      { id: 74, name: '琴詠ニア', style: 'ノーマル', description: 'VIP：年輕清亮' },
      { id: 94, name: '中部つるぎ', style: 'ノーマル', description: 'VIP：成熟專業' }
    ]
  } as const

  return vipMap[gender] || []
}

// 語音服務的高級錯誤處理和回退機制
export class TTSServiceManager {
  private static instance: TTSServiceManager
  private services: TTSService[] = []
  private currentServiceIndex = 0
  private serviceAvailabilityCache = new Map<string, { available: boolean, timestamp: number }>()
  private readonly CACHE_DURATION = 30000 // 30秒快取
  private lastInvokeAt = new Map<string, number>() // 簡易節流
  private readonly THROTTLE_MS = 300 // 300ms 連點防抖

  private constructor() {
    // 初始化可用的語音服務
    // 使用你的 VPS 服務器地址
    const voicevoxUrl = VOICEVOX_BASE_URL
    
    // 創建基礎語音服務
    const baseVoicevoxService = new VoicevoxTTSService({ baseUrl: voicevoxUrl })
    
    this.services = [
      baseVoicevoxService,
      new BrowserTTSService()
    ]
    
    // console.log('🎤 TTSServiceManager 初始化，VOICEVOX URL:', voicevoxUrl)
  }

  // 單例模式
  public static getInstance(): TTSServiceManager {
    if (!TTSServiceManager.instance) {
      TTSServiceManager.instance = new TTSServiceManager()
    }
    return TTSServiceManager.instance
  }

  // 檢查當前服務是否可用
  private async checkCurrentServiceAvailability(): Promise<boolean> {
    try {
      const currentService = this.services[this.currentServiceIndex]
      if (currentService.isServiceAvailable) {
        const status = await currentService.isServiceAvailable()
        return status.available
      }
      return true // 如果沒有 isServiceAvailable 方法，假設可用
    } catch (error) {
      console.warn('當前語音服務可用性檢查失敗', error)
      return false
    }
  }

  // 公開的服務可用性檢查方法
  async isServiceAvailable(): Promise<boolean> {
    return this.checkCurrentServiceAvailability()
  }

  // 切換到下一個可用的語音服務
  private async switchToNextService(): Promise<TTSService | null> {
    const originalIndex = this.currentServiceIndex
    
    // 嘗試找到下一個可用的服務
    do {
      this.currentServiceIndex = (this.currentServiceIndex + 1) % this.services.length
      
      // 如果已經遍歷了所有服務，仍然沒有找到可用的服務
      if (this.currentServiceIndex === originalIndex) {
        console.error('所有語音服務都不可用')
        return null
      }

      const isAvailable = await this.checkCurrentServiceAvailability()
      if (isAvailable) {
        return this.services[this.currentServiceIndex]
      }
    } while (this.currentServiceIndex !== originalIndex)

    return null
  }

  // 智能語音播放方法
  public async speak(
    text: string, 
    options: {
      voice?: 'male' | 'female',
      language?: string,
      voice_id?: number,
      fallbackToBrowser?: boolean,
      role?: string
    } = {}
  ): Promise<void> {
    // 節流：同一段文字+voice_id 在 300ms 內重複呼叫直接忽略
    const key = `${text}__${options.voice_id ?? 'default'}`
    const now = Date.now()
    const last = this.lastInvokeAt.get(key) || 0
    if (now - last < this.THROTTLE_MS) {
      return
    }
    this.lastInvokeAt.set(key, now)

    let attempts = 0
    const maxAttempts = this.services.length

    while (attempts < maxAttempts) {
      try {
        const currentService = this.services[this.currentServiceIndex]
        
        // console.log(`嘗試使用 ${currentService.constructor.name} 播放語音`)
        
        await currentService.speak(text, {
          voice: options.voice || 'female',
          language: options.language || 'ja-JP',
          voice_id: options.voice_id,
          role: options.role
        })

        // 播放成功，退出循環
        return
      } catch (error) {
        console.warn(`語音服務 ${this.services[this.currentServiceIndex].constructor.name} 播放失敗`, error)
        
        // 切換到下一個服務
        const nextService = await this.switchToNextService()
        
        if (!nextService) {
          // 如果沒有可用的服務，且明確允許回退到瀏覽器語音
          if (options.fallbackToBrowser === true) {
            console.warn('所有語音服務不可用，嘗試使用瀏覽器語音')
            await this.services[this.services.length - 1].speak(text, options)
            return
          }
          
          throw new Error('所有語音服務都不可用')
        }

        attempts++
      }
    }

    throw new Error('無法找到可用的語音服務')
  }

  // 獲取可用語音列表
  public async getAvailableVoices(): Promise<Array<{
    id: string,
    name: string,
    language: string,
    gender: 'male' | 'female',
    service: string
  }>> {
    const availableVoices: any[] = []

    for (const service of this.services) {
      try {
        const voices = await service.getAvailableVoices()
        availableVoices.push(
          ...voices.map(voice => ({
            ...voice,
            service: service.constructor.name
          }))
        )
      } catch (error) {
        console.warn(`獲取 ${service.constructor.name} 語音列表失敗`, error)
      }
    }

    return availableVoices
  }

  // 獲取服務實例（用於新的語音服務）
  public getService(index: number): TTSService | undefined {
    return this.services[index]
  }

  // 檢查服務可用性（帶快取）
  public async isServiceAvailableCached(serviceIndex: number): Promise<boolean> {
    const service = this.services[serviceIndex]
    if (!service) return false

    const cacheKey = `service_${serviceIndex}`
    const now = Date.now()
    const cached = this.serviceAvailabilityCache.get(cacheKey)

    // 如果快取有效，直接返回
    if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
      return cached.available
    }

    // 檢查服務可用性
    try {
      if (!service.isServiceAvailable) {
        return true // 如果沒有 isServiceAvailable 方法，假設可用
      }
      
      const result = await service.isServiceAvailable()
      const available = result?.available || false
      
      // 更新快取
      this.serviceAvailabilityCache.set(cacheKey, {
        available,
        timestamp: now
      })
      
      return available
    } catch (error) {
      console.warn(`服務 ${serviceIndex} 可用性檢查失敗:`, error)
      return false
    }
  }
}

// 惰性單例：避免模組載入時就初始化（最小化環境下可避免初始化階段錯誤）
let __ttsManager: TTSServiceManager | null = null
export function getTTSServiceManager(): TTSServiceManager {
  if (!__ttsManager) {
    __ttsManager = TTSServiceManager.getInstance()
  }
  return __ttsManager
}

// 手動性別映射
const MANUAL_GENDER_MAPPING: Record<string, 'male' | 'female' | 'other'> = {
    // 女性角色
    '四国めたん': 'female', // 2, 0, 6, 4, 36, 37
    'ずんだもん': 'female', // 3, 1, 7, 5, 22, 38, 75, 76
    '春日部つむぎ': 'female', // 8
    '雨晴はう': 'female', // 10
    '波音リツ': 'female', // 9, 65
    '冥鳴ひまり': 'female', // 14
    '九州そら': 'female', // 16, 15, 18, 17, 19
    'もち子さん': 'female', // 20, 66, 77, 78, 79, 80
    'WhiteCUL': 'female', // 23, 24, 25, 26
    '櫻歌ミコ': 'female', // 43, 44, 45
    '小夜/SAYO': 'female', // 46
    '春歌ナナ': 'female', // 54
    '猫使アル': 'female', // 55, 56, 57, 110, 111
    '猫使ビィ': 'female', // 58, 59, 60, 112
    '中国うさぎ': 'female', // 61, 62, 63, 64
    'あいえるたん': 'female', // 68

    '琴詠ニア': 'female', // 74
    'ぞん子': 'female', // 90, 91, 92, 93
    '中部つるぎ': 'female', // 94, 95, 96, 97, 98
    'ユーレイちゃん': 'female', // 102, 103, 104, 105, 106
    '東北ずん子': 'female', // 107
    '東北きりたん': 'female', // 108
    '東北イタコ': 'female', // 109
    'No.7': 'other', // 29, 30, 31
  
    // 男性角色
    '玄野武宏': 'male', // 11, 39, 40, 41
    '白上虎太郎': 'male', // 12, 32, 33, 34, 35
    '青山龍星': 'male', // 13, 81, 82, 83, 84, 85, 86
    'ちび式じい': 'male', // 42
    '雀松朱司': 'male', // 52
    '麒ヶ島宗麟': 'male', // 53
    '黒沢冴白': 'male', // 100
    '†聖騎士 紅桜†': 'male', // 51
    '栗田まろん': 'male', // 67
    '剣崎雌雄': 'male', // 21
    '満別花丸': 'male', // 69, 70, 71, 72, 73
    '離途': 'male', // 99, 101
  
    // 其他角色
    '後鬼': 'other', // 27, 28, 87, 88
    'ナースロボ＿タイプＴ': 'other', // 47, 48, 49, 50
    'Voidoll': 'other', // 89

  
  // 可以根據實際情況持續擴充
};

export async function fetchAllVoicevoxVoices(): Promise<{
  male: Array<{
    id: number, 
    name: string, 
    style: string,
    description?: string,
    gender?: string,
    styles?: string[],
    allStyles?: { id: number, style: string }[],
    main_avatar_path?: string
  }>,
  female: Array<{
    id: number, 
    name: string, 
    style: string,
    description?: string,
    gender?: string,
    styles?: string[],
    allStyles?: { id: number, style: string }[],
    main_avatar_path?: string
  }>,
  other: Array<{
    id: number, 
    name: string, 
    style: string,
    description?: string,
    gender?: string,
    styles?: string[],
    allStyles?: { id: number, style: string }[],
    main_avatar_path?: string
  }>
}> {
  try {
    // ✅ 使用內部 API 代理，避免 CORS 問題
    const response = await fetch('/api/voicevox/speakers', {
      method: 'GET',
      signal: AbortSignal.timeout(10000)
    });

    if (!response.ok) {
      console.error(`獲取 Voicevox 語音角色失敗：${response.status} ${response.statusText}`);
      throw new Error(`服務無法訪問：${response.status} ${response.statusText}`);
    }

    const speakers = await response.json();
    
    // 從 Supabase 獲取已設置的語音角色風格和圖片
    const { data: supabaseRoles, error: rolesError } = await supabase
      .from('voice_roles')
      .select('original_name, voice_style, main_avatar_path, use_style_avatars, style_avatar_paths, gender')
      .eq('is_active', true);

    if (rolesError) {
      console.error('獲取 Supabase 角色失敗:', rolesError);
    }

    const voicesByGender: {
      male: Array<{
        id: number, 
        name: string, 
        style: string,
        description?: string,
        gender?: string,
        styles?: string[],
        allStyles?: { id: number, style: string }[],
        main_avatar_path?: string
      }>,
      female: Array<{
        id: number, 
        name: string, 
        style: string,
        description?: string,
        gender?: string,
        styles?: string[],
        allStyles?: { id: number, style: string }[],
        main_avatar_path?: string
      }>,
      other: Array<{
        id: number, 
        name: string, 
        style: string,
        description?: string,
        gender?: string,
        styles?: string[],
        allStyles?: { id: number, style: string }[],
        main_avatar_path?: string
      }>
    } = { 
      male: [], 
      female: [], 
      other: [] 
    };

    const processedNames = new Set<string>();

    speakers.forEach((speaker: any) => {
      // 優先使用手動映射
      let detectedGender = MANUAL_GENDER_MAPPING[speaker.name];
      
      // 如果手動映射未定義，嘗試自動檢測
      if (!detectedGender) {
        const femaleKeywords = ['めたん', 'ニア', 'つむぎ', '雨晴', '波音'];
        const maleKeywords = ['武宏', '龍星', '虎太郎'];
        
        if (femaleKeywords.some(keyword => speaker.name.includes(keyword))) {
          detectedGender = 'female';
        } else if (maleKeywords.some(keyword => speaker.name.includes(keyword))) {
          detectedGender = 'male';
        } else {
          detectedGender = 'other';
        }
      }

      // 避免重複處理同名角色
      if (processedNames.has(speaker.name)) return;
      processedNames.add(speaker.name);

      // 找到對應的 Supabase 角色
      const supabaseRole = supabaseRoles?.find(role => (role as any).original_name === speaker.name);

      // 提取所有風格
      const allStyles = speaker.styles.map((style: any) => ({
        id: style.id,
        style: style.name
      }));

      const voiceEntry = {
        id: speaker.styles[0].id,
        name: speaker.name,
        style: speaker.styles[0].name,
        description: `${speaker.name} - 語音角色`,
        gender: detectedGender,
        styles: speaker.styles.map((s: any) => s.name),
        allStyles: allStyles,
        main_avatar_path: (supabaseRole as any)?.main_avatar_path
      };

      voicesByGender[detectedGender].push(voiceEntry);
    });

    // console.log('提取的唯一語音角色:', voicesByGender);
    return voicesByGender;
  } catch (error) {
    console.error('獲取語音角色失敗，回退到預設角色:', error);
    
    // 如果獲取失敗，回退到原有的 VOICEVOX_VOICES
    const uniqueVoices: {
      male: Array<{
        id: number, 
        name: string, 
        style: string,
        description?: string,
        gender?: string,
        styles?: string[],
        allStyles?: { id: number, style: string }[],
        main_avatar_path?: string
      }>,
      female: Array<{
        id: number, 
        name: string, 
        style: string,
        description?: string,
        gender?: string,
        styles?: string[],
        allStyles?: { id: number, style: string }[],
        main_avatar_path?: string
      }>,
      other: Array<{
        id: number, 
        name: string, 
        style: string,
        description?: string,
        gender?: string,
        styles?: string[],
        allStyles?: { id: number, style: string }[],
        main_avatar_path?: string
      }>
    } = { male: [], female: [], other: [] }

    Object.entries(VOICEVOX_VOICES).forEach(([gender, voices]) => {
      const genderKey = gender as 'male' | 'female' | 'other';
      voices.forEach(voice => {
        const existingVoice = uniqueVoices[genderKey].find((v: any) => v.name === voice.name)
        
        if (!existingVoice) {
          // 如果是新角色，創建新的條目
          uniqueVoices[genderKey].push({
            id: voice.id,
            name: voice.name,
            style: voice.style,
            description: voice.description,
            gender: genderKey,
            styles: [voice.style],
            allStyles: [{ id: voice.id, style: voice.style }]
          })
        } else {
          // 如果角色已存在，添加新的風格
          existingVoice.styles = existingVoice.styles || []
          if (!existingVoice.styles.includes(voice.style)) {
            existingVoice.styles.push(voice.style)
          }
          existingVoice.allStyles = existingVoice.allStyles || []
          existingVoice.allStyles.push({ id: voice.id, style: voice.style })
        }
      })
    })

    return uniqueVoices;
  }
}

// 在需要時可以直接調用這個函數獲取所有語音角色
export const getAllVoicevoxVoices = fetchAllVoicevoxVoices;

// 導出 VoicevoxTTSService 實例（兼容舊代碼）
export const voicevoxTTSService = new VoicevoxTTSService();

// 導出新的語音服務類別
export { UserPreferenceTTSService } from './UserPreferenceTTSService'
export { RoleBasedTTSService } from './RoleBasedTTSService'
